import { Link } from 'react-router';
import { useAuth } from '../../../lib/auth';
import { BookOpen, Calendar, Handshake, HandHelping, Mail, Tag, Users, ShieldCheck, ScrollText, FileText } from 'lucide-react';

interface DocLink {
  to: string;
  icon: typeof BookOpen;
  title: string;
  description: string;
}

function DocCard({ to, icon: Icon, title, description }: DocLink) {
  return (
    <Link
      to={to}
      className="flex items-start gap-4 rounded-xl border bg-white p-5 shadow-sm transition-all hover:border-[var(--vivid-purple)]/30 hover:shadow-md"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[var(--vivid-purple)]/10">
        <Icon className="h-5 w-5 text-[var(--vivid-purple)]" />
      </div>
      <div>
        <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>{title}</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
    </Link>
  );
}

const STAFF_DOCS: DocLink[] = [
  { to: '/staff/documentatie/activitati', icon: Calendar, title: 'Activități', description: 'Gestionarea activităților: creare, editare, publicare, calendar, export.' },
  { to: '/staff/documentatie/participanti', icon: Users, title: 'Participanți', description: 'Listele de participanți, anulare înregistrări, printare și export.' },
  { to: '/staff/documentatie/voluntari', icon: HandHelping, title: 'Voluntari', description: 'Gestionarea voluntarilor: aplicații, aprobare, echipe, coordonatori, notificări.' },
  { to: '/staff/documentatie/invitatii', icon: Mail, title: 'Invitații', description: 'Activități doar cu invitație: trimitere, CSV import, acceptare/refuzare.' },
  { to: '/staff/documentatie/parteneri', icon: Handshake, title: 'Prieteni', description: 'Gestionarea prietenilor, validare, date legale, revocare DPA.' },
  { to: '/staff/documentatie/taguri', icon: Tag, title: 'Tag-uri', description: 'Tag-uri principale și secundare, culori și iconuri.' },
  { to: '/staff/documentatie/echipe', icon: ShieldCheck, title: 'Echipe', description: 'Gestionarea echipelor de lucru (superadmin).' },
  { to: '/staff/documentatie/utilizatori', icon: Users, title: 'Utilizatori', description: 'Gestionarea utilizatorilor staff, invitații și roluri (superadmin).' },
  { to: '/staff/documentatie/jurnal', icon: ScrollText, title: 'Jurnal de audit', description: 'Monitorizarea modificărilor asupra activităților (superadmin).' },
];

const PARTNER_DOCS: DocLink[] = [
  { to: '/staff/documentatie/activitatile-mele', icon: Calendar, title: 'Activitățile mele', description: 'Vizualizarea și editarea activităților organizate de tine.' },
  { to: '/staff/documentatie/organizatiile-mele', icon: Handshake, title: 'Organizațiile mele', description: 'Editarea profilului organizației și a datelor legale.' },
  { to: '/staff/documentatie/dpa', icon: FileText, title: 'Acord de Prelucrare a Datelor (DPA)', description: 'Ce este DPA, de ce e necesar, cum se semnează și ce implică.' },
];

export default function DocsIndex() {
  const { user } = useAuth();
  const isStaff = user?.is_staff;
  const isPartner = user?.partner_ids && user.partner_ids.length > 0;

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 flex items-center gap-3">
        <BookOpen className="h-6 w-6 text-[var(--vivid-purple)]" />
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
          Documentație
        </h1>
      </div>

      {isStaff && (
        <section className="mb-8">
          <h2 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Zona Staff
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {STAFF_DOCS.map((doc) => (
              <DocCard key={doc.to} {...doc} />
            ))}
          </div>
        </section>
      )}

      {isPartner && (
        <section className="mb-8">
          <h2 className="mb-4 text-lg font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            Zona Prieten
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {PARTNER_DOCS.map((doc) => (
              <DocCard key={doc.to} {...doc} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
